<script setup lang="ts">
  import { ref, onMounted } from 'vue'
  import InputField from '@/components/admin/form/InputField.vue'
  import { useTranslation } from '@/composables/useTranslation'
  import {
    OrganisationsService,
    type CustomerOrganisation,
    type PatchedCustomerOrganisationRequest
  } from '@/types/generated'
  import * as yup from 'yup'
  import SettingsPage from '@/components/app/settings/SettingsPage.vue'
  import AutoSavingForm from '@/components/admin/form/AutoSavingForm.vue'
  import { genericErrorToast } from '@/lib/toasts'
  import { Skeleton } from '@/components/ui/skeleton'

  const { t } = useTranslation({
    en: {
      title: 'Law Firm Settings',
      subtitle: 'Manage your law firm settings and information.',
      lawFirmName: 'Law Firm Name',
      street: 'Street',
      houseNumber: 'House Number',
      zip: 'ZIP',
      city: 'City',
      country: 'Country',
      phone: 'Phone Number',
      email: 'Email',
      autoSaveMessage: 'Changes are saved automatically',
      loadError: 'Failed to load organization data. Please try again.',
      invalidZip: 'Invalid ZIP code'
    },
    de: {
      title: 'Kanzlei Einstellungen',
      subtitle: 'Verwalten Sie die Einstellungen und Informationen Ihrer Kanzlei.',
      lawFirmName: 'Name der Kanzlei',
      street: 'Straße',
      houseNumber: 'Hausnr.',
      zip: 'PLZ',
      city: 'Stadt',
      country: 'Land',
      phone: 'Telefonnummer',
      email: 'E-Mail',
      autoSaveMessage: 'Änderungen werden automatisch gespeichert',
      loadError: 'Organisationsdaten konnten nicht geladen werden. Bitte versuchen Sie es erneut.',
      invalidZip: 'Ungültige PLZ'
    }
  })

  const org = ref<CustomerOrganisation | null>(null)
  const isLoading = ref(true)
  const hasError = ref(false)

  onMounted(async () => {
    try {
      org.value = (await OrganisationsService.organisationsRetrieve({
        uuid: 'current'
      })) as CustomerOrganisation
    } catch (error) {
      console.error('Failed to load organization:', error)
      hasError.value = true
      genericErrorToast()
    } finally {
      isLoading.value = false
    }
  })

  const handleSave = async (values: any) => {
    if (isLoading.value) return
    try {
      await OrganisationsService.organisationsPartialUpdate({
        uuid: 'current',
        requestBody: values as PatchedCustomerOrganisationRequest
      })
    } catch (error) {
      genericErrorToast()
    }
  }
</script>

<template>
  <SettingsPage>
    <!-- Loading Skeleton -->
    <div
      v-if="isLoading"
      class="space-y-6 px-2 py-4 sm:px-0"
    >
      <div class="mb-6">
        <Skeleton class="mb-2 h-6 w-48" />
        <Skeleton class="h-4 w-72" />
      </div>
      <div class="space-y-6">
        <!-- Law firm name skeleton -->
        <div class="space-y-2">
          <Skeleton class="h-4 w-32" />
          <Skeleton class="h-10 w-full" />
        </div>
        <!-- Street + house number skeleton -->
        <div class="grid grid-cols-1 gap-4 sm:grid-cols-6 sm:gap-6">
          <div class="space-y-2 sm:col-span-4">
            <Skeleton class="h-4 w-20" />
            <Skeleton class="h-10 w-full" />
          </div>
          <div class="space-y-2 sm:col-span-2">
            <Skeleton class="h-4 w-24" />
            <Skeleton class="h-10 w-full" />
          </div>
        </div>
        <!-- ZIP + City + Country skeleton -->
        <div class="grid grid-cols-1 gap-4 sm:grid-cols-3 sm:gap-6">
          <div class="space-y-2">
            <Skeleton class="h-4 w-12" />
            <Skeleton class="h-10 w-full" />
          </div>
          <div class="space-y-2">
            <Skeleton class="h-4 w-16" />
            <Skeleton class="h-10 w-full" />
          </div>
          <div class="space-y-2">
            <Skeleton class="h-4 w-20" />
            <Skeleton class="h-10 w-full" />
          </div>
        </div>
        <!-- Phone + Email skeleton -->
        <div class="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-6">
          <div class="space-y-2">
            <Skeleton class="h-4 w-28" />
            <Skeleton class="h-10 w-full" />
          </div>
          <div class="space-y-2">
            <Skeleton class="h-4 w-16" />
            <Skeleton class="h-10 w-full" />
          </div>
        </div>
      </div>
    </div>

    <!-- Error State -->
    <div
      v-else-if="hasError"
      class="flex flex-col items-center justify-center py-12 text-center"
    >
      <p class="text-gray-600">{{ t('loadError') }}</p>
    </div>

    <!-- Form Content -->
    <AutoSavingForm
      v-else-if="org"
      @save="handleSave"
    >
      <div class="px-2 py-4 sm:px-0">
        <!-- Header -->
        <div class="mb-6">
          <h2 class="text-base font-semibold leading-6 text-gray-900">{{ t('title') }}</h2>
          <p class="mt-1 text-sm text-gray-500">{{ t('subtitle') }}</p>
        </div>

        <form
          class="space-y-6"
          @submit.prevent
        >
          <!-- Law Firm Name - Full width -->
          <div>
            <InputField
              id="name"
              v-model="org.name"
              name="name"
              :label="t('lawFirmName')"
            />
          </div>

          <!-- Street + House Number -->
          <div class="grid grid-cols-1 gap-4 sm:grid-cols-6 sm:gap-6">
            <div class="sm:col-span-4">
              <InputField
                id="street"
                v-model="org.street"
                name="street"
                :label="t('street')"
              />
            </div>
            <div class="sm:col-span-2">
              <InputField
                id="houseNumber"
                v-model="org.street_number"
                name="street_number"
                :label="t('houseNumber')"
              />
            </div>
          </div>

          <!-- ZIP + City + Country -->
          <div class="grid grid-cols-2 gap-4 sm:grid-cols-3 sm:gap-6">
            <div class="col-span-1">
              <InputField
                id="zip"
                v-model="org.zip"
                :rules="
                  yup
                    .string()
                    .matches(/^\d{5}$/, t('invalidZip'))
                    .required()
                "
                name="zip"
                :label="t('zip')"
              />
            </div>
            <div class="col-span-1">
              <InputField
                id="city"
                v-model="org.city"
                name="city"
                :label="t('city')"
              />
            </div>
            <div class="col-span-2 sm:col-span-1">
              <InputField
                id="country"
                v-model="org.country"
                name="country"
                :label="t('country')"
              />
            </div>
          </div>

          <!-- Phone + Email -->
          <div class="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-6">
            <div>
              <InputField
                id="phone"
                v-model="org.phone"
                type="tel"
                name="phone"
                :label="t('phone')"
              />
            </div>
            <div>
              <InputField
                id="email"
                v-model="org.email"
                type="email"
                :rules="yup.string().email().required()"
                name="email"
                :label="t('email')"
              />
            </div>
          </div>
        </form>
      </div>

      <!-- Auto-save indicator -->
      <div
        class="fixed bottom-4 left-4 z-50 flex items-center gap-2 rounded-lg bg-white px-3 py-2 shadow-lg
          sm:bottom-4 sm:left-auto sm:right-4 sm:px-4 sm:py-3"
      >
        <div class="h-2 w-2 animate-pulse rounded-full bg-yellow-400" />
        <span class="text-xs text-gray-600 sm:text-sm">{{ t('autoSaveMessage') }}</span>
      </div>
    </AutoSavingForm>
  </SettingsPage>
</template>
